import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ThumbsUp, ThumbsDown, Minus, SkipForward,
  MessageCircle, Flag, Bookmark, Download,
  Users, Clock, BookmarkCheck, Pencil, Trash2, FlagOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DownloadCard from './DownloadCard';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_COMMENT_LENGTH = 280;

const PostCard = ({
  post,
  onSkip,
  onVoteComplete,
  onAuthRequired,
  isPublic = false,
  isSaved = false,
  onUnsave,
  compact = false,
  hideSkip = false,
}) => {
  const { token, user } = useAuth();
  const [voted, setVoted] = useState(post.user_vote || null);
  const [votes, setVotes] = useState(post.votes || { total: 0 });
  const [countdown, setCountdown] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [hasCommented, setHasCommented] = useState(false);
  const [showDownloadCard, setShowDownloadCard] = useState(false);
  const [reported, setReported] = useState(false);
  const [saved, setSaved] = useState(isSaved);

  useEffect(() => { setSaved(isSaved); }, [isSaved]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && onVoteComplete) {
      onVoteComplete();
    }
  }, [countdown, onVoteComplete]);

  const handleVote = async (voteType) => {
    if (isPublic) { onAuthRequired?.(); return; }
    try {
      const res = await axios.post(
        `${API}/posts/${post.id}/vote`,
        { vote_type: voteType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoted(voteType);
      setVotes(res.data.votes);
      toast.success('Vote recorded!');
      setCountdown(10);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to vote');
    }
  };

  const handleReport = async () => {
    if (isPublic) { onAuthRequired?.(); return; }
    try {
      await axios.post(`${API}/posts/${post.id}/report`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setReported(true);
      toast.success('Report submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to report');
    }
  };

  const handleSave = async () => {
    if (isPublic) { onAuthRequired?.(); return; }
    try {
      await axios.post(`${API}/posts/${post.id}/save`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      toast.success('Post saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleUnsaveLocal = async () => {
    try {
      await axios.delete(`${API}/posts/${post.id}/save`, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(false);
      toast.success('Post removed from saved');
      onUnsave?.();
    } catch (err) {
      toast.error('Failed to unsave post');
    }
  };

  const loadComments = async () => {
    if (isPublic) { onAuthRequired?.(); return; }
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(res.data.comments);
      setShowComments(true);
      if (res.data.comments.find(c => c.user_id === user?.id)) setHasCommented(true);
    } catch (err) {
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (newComment.length > MAX_COMMENT_LENGTH) { toast.error('Comment too long'); return; }
    try {
      await axios.post(`${API}/posts/${post.id}/comment`, { content: newComment }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Comment added');
      setNewComment('');
      setHasCommented(true);
      loadComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to comment');
    }
  };

  const canEditComment = (comment) => comment.user_id === user?.id;
  const canDeleteComment = (comment) => comment.user_id === user?.id || post.user_id === user?.id;
  const startEditComment = (comment) => { setEditingCommentId(comment.id); setEditText(comment.content); };
  const cancelEdit = () => { setEditingCommentId(null); setEditText(''); };

  const saveEditComment = async (commentId) => {
    if (!editText.trim() || editText.length > MAX_COMMENT_LENGTH) return;
    try {
      await axios.put(`${API}/comments/${commentId}`, { content: editText }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, content: editText } : c));
      cancelEdit();
      toast.success('Comment updated');
    } catch { toast.error('Failed to update comment'); }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch { toast.error('Failed to delete comment'); }
  };

  const getTimeAgo = (timestamp) => {
    const diff = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const isPremium = post.author_role === 'premium';
  const isPromoted = post.promoted;
  const showSkip = !hideSkip && !isSaved && !!onSkip;

  return (
    <div className={`w-full mx-auto min-w-0 ${compact ? 'max-w-full px-2 sm:px-0' : 'max-w-2xl'}`}>
      <div
        data-testid="post-card"
        className={`relative bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden border-2 ${
          isPremium ? 'border-transparent' : isPromoted ? 'border-yellow-200' : 'border-gray-200 dark:border-slate-700'
        }`}
      >
        {isPremium && <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-blue-500/10 animate-pulse" />}

        <div className="relative p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Link
              to={user && post.author_username === user.username ? '/profile' : `/profile/${post.author_username}`}
              className="flex items-center space-x-3 group"
            >
              <img src={post.author_avatar} alt={post.author_username} className={`w-12 h-12 rounded-full ${isPremium ? 'ring-4 ring-purple-400' : ''}`} />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 group-hover:underline">
                  {post.author_username}
                  {isPremium && <span className="text-yellow-500">⭐</span>}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-slate-400">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{votes.total || 0}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{getTimeAgo(post.created_at)}</span>
                </div>
              </div>
            </Link>
            {isPromoted && (
              <div className="flex items-center gap-1 px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs font-medium rounded-full">
                ⚡ Promoted
              </div>
            )}
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-lg text-gray-800 dark:text-slate-100 whitespace-pre-wrap break-words leading-relaxed">{post.content}</p>
          </div>

          {/* Vote Results */}
          {voted && votes.agree_percent !== undefined && (
            <div className="mb-6 space-y-3 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
              {[
                { label: 'Agree', pct: votes.agree_percent, color: 'bg-green-500', text: 'text-green-600' },
                { label: 'Disagree', pct: votes.disagree_percent, color: 'bg-red-500', text: 'text-red-600' },
                { label: 'Neutral', pct: votes.neutral_percent, color: 'bg-gray-400', text: 'text-gray-500 dark:text-slate-400' },
              ].map(({ label, pct, color, text }) => (
                <div key={label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={`${text} font-medium`}>{label}</span>
                    <span className="font-semibold dark:text-white">{pct}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                    <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vote / Download Buttons */}
          <div className="mb-4">
            {!voted ? (
              <div className="flex items-center gap-2">
                <Button onClick={() => handleVote('agree')} className="flex-1 bg-green-500 hover:bg-green-600 text-white" disabled={isPublic}>
                  <ThumbsUp className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Agree</span>
                </Button>
                <Button onClick={() => handleVote('disagree')} className="flex-1 bg-red-500 hover:bg-red-600 text-white" disabled={isPublic}>
                  <ThumbsDown className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Disagree</span>
                </Button>
                <Button onClick={() => handleVote('neutral')} className="flex-1 bg-gray-500 hover:bg-gray-600 text-white" disabled={isPublic}>
                  <Minus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Neutral</span>
                </Button>
              </div>
            ) : (
              <Button onClick={() => setShowDownloadCard(true)} variant="outline" className="w-full">
                <Download className="w-4 h-4 mr-2" />Download Card
              </Button>
            )}
          </div>

          {/* Bottom Bar */}
          <div className="flex items-center justify-between gap-2">
            {showSkip ? (
              <Button onClick={onSkip} variant="outline" size="sm">
                <SkipForward className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Skip</span>
              </Button>
            ) : <div />}

            <div className="flex items-center gap-1">
              <Button onClick={loadComments} variant="ghost" size="sm" className="gap-1">
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm">{post.comment_count || 0}</span>
              </Button>
              <Button
                onClick={handleReport}
                variant="ghost"
                size="sm"
                title={reported ? 'Reported' : 'Report'}
                disabled={reported}
                className={reported ? 'text-red-500 opacity-70' : 'text-gray-500 hover:text-red-500'}
              >
                {reported ? <FlagOff className="w-4 h-4" /> : <Flag className="w-4 h-4" />}
              </Button>
              <Button
                onClick={saved ? handleUnsaveLocal : handleSave}
                variant="ghost"
                size="sm"
                title={saved ? 'Unsave' : 'Save'}
                className={saved ? 'text-cyan-600' : 'text-gray-500 hover:text-cyan-600'}
              >
                {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Countdown */}
          {countdown !== null && countdown > 0 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <p className="text-sm text-gray-500 dark:text-slate-400">Next post in {countdown}s</p>
              <Button onClick={() => setCountdown(null)} variant="outline" size="sm">Cancel</Button>
            </div>
          )}
        </div>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-4 p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Comments ({comments.length}/25)</h3>
          {comments.length > 0 ? (
            <div className="space-y-4 mb-5 max-h-96 overflow-y-auto pr-1">
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-3 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl border border-gray-100 dark:border-slate-700">
                  <img src={comment.avatar_url} alt={comment.username} className="w-9 h-9 rounded-full flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Link
                          to={user && comment.username === user.username ? '/profile' : `/profile/${comment.username}`}
                          className="font-medium text-sm text-gray-900 dark:text-white hover:underline"
                        >
                          {comment.username}
                        </Link>
                        <span className="text-xs text-gray-400">{getTimeAgo(comment.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {canEditComment(comment) && (
                          <button
                            onClick={() => startEditComment(comment)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDeleteComment(comment) && (
                          <button
                            onClick={() => deleteComment(comment.id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingCommentId === comment.id ? (
                      <div className="space-y-2">
                        <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveEditComment(comment.id)}>Save</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 dark:text-slate-300 text-sm break-words whitespace-pre-wrap" style={{ overflowWrap: 'anywhere' }}>
                        {comment.content}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center mb-5">No comments yet</p>
          )}
          {comments.length < 25 && !hasCommented && (
            <form onSubmit={handleComment} className="space-y-2">
              <Textarea placeholder="Add a comment..." value={newComment} maxLength={MAX_COMMENT_LENGTH} onChange={(e) => setNewComment(e.target.value)} rows={2} />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{MAX_COMMENT_LENGTH - newComment.length} left</span>
                <Button type="submit" size="sm">Post Comment</Button>
              </div>
            </form>
          )}
          {hasCommented && <p className="text-center text-sm text-gray-500 py-2">You've already commented on this post</p>}
        </div>
      )}

      {showDownloadCard && voted && (
        <DownloadCard post={post} votes={votes} userVote={voted} isPremium={post.author_role === 'premium'} onClose={() => setShowDownloadCard(false)} />
      )}
    </div>
  );
};

export default PostCard;